import { useState, useRef, useEffect } from 'react';
import ExcelJS from "exceljs";
import { useDispatch, useSelector } from 'react-redux';
import { SeverityLevels, ReportsCategory, DATE_FORMAT } from '../../../../../constants';
import { dateTimeFormater } from '../../../../../utils/date_utils';

const getDefaultMetadata = (metadata = {}) => ({
    title: metadata?.title,
    room: metadata?.room,
    description: metadata.description || '',
    price: metadata.price || '0',
});


const useXlsxGenerator = (handleNotifState, handleCloseMenu) => {
    const [loading, setLoading] = useState(false);
    const isMounted = useRef(true); // Track if the component is mounted
    
    const { 
        checkboxStates, 
        itemMetadata, 
        header, 
        date, 
        reportData, 
        orderedData,
        roomsData,
        classifiedCategories,
        includeVideoInExport,
        hasBaseline, 
        editExportMode,
        finishExportMode,
        guestData,
        inspectionType,
        centralizedView,
        videoSrc,
        exportReport
    } = useSelector((state) => state.reports);
    
    const { customText, selectedCurrency } = exportReport;

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; }; // Cleanup on unmount
    }, []);

    const borderAll = () => ({
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" }
    });

    const setCellStyling = ({cell, value=null, fill=null, font=null, alignment=null, border=null}) => {
        if (value) cell.value = value;
        if (font) cell.font = font;
        if (fill) cell.fill = fill;
        if (alignment) cell.alignment = alignment;
        if (border) cell.border = border;
    };

    const prepFuckingData = (reportItems, includeBefore) => {
        // Iterating over the rooms
        const roomsList = Object.keys(reportItems.items);
        const allRoomsClassifications = {};
        const allRC_Items = {};
        const allRC_Rows = {};
        for (let roomIdx=0; roomIdx < roomsList.length; roomIdx++) {
            const room = roomsList[roomIdx];
            if ((reportItems.items[room]?.items || []).length === 0) continue;

            const roomsClassifications = Object.keys(reportItems.items[room]?.classifications || {});
            allRoomsClassifications[room] = roomsClassifications;
            if (roomsClassifications.length === 0) continue;

            const classificationItems = roomsClassifications.reduce((acc, g) => ({
                ...acc,
                [g]: reportItems.items[room]?.items.filter((item) => item.classifiedCategory === g)
            }), {});
            allRC_Items[room] = classificationItems;

            // Iterating over the group items in each room
            for (let gIdx=0; gIdx < roomsClassifications.length; gIdx++) {
                // "Area", "Condition", "Comment", "Price", ?"Before Media", "Media"
                const groupName = roomsClassifications[gIdx];
                const groupItems = classificationItems[groupName];
                const hasDamages = groupItems.some((item) => item.issue_type === 'Defect');
                const groupCondition = hasDamages ? "Attention Required" : "OK";
                const groupRow = [
                    groupName, groupCondition, 
                    reportItems.items[room].classifications[groupName]?.description,
                    parseFloat(reportItems.items[room].classifications[groupName]?.price || 0),
                ];
                const beforeImages = [];
                const afterImages = [];
                for (let itemIdx=0; itemIdx < groupItems.length; itemIdx++) {
                    const groupItem = groupItems[itemIdx];
                    if (includeBefore) {
                        beforeImages.append(groupItem.img_before);
                        // groupRow.push(groupItem.img_before);
                    }
                    afterImages.push(groupItem.img);
                    // groupRow.push(groupItem.img);
                }
                if (includeBefore) {
                    groupRow.push(beforeImages);
                } 
                groupRow.push(afterImages);
                allRC_Rows[`${room}-${groupName}`] = groupRow;
            }
        }

        return {
            roomsList,
            allRoomsClassifications,
            allRC_Items,
            allRC_Rows
        };
    };

    const buildMediaWorksheet = (workbook, preppedFuckingData, includeBefore, baseStyle) => {
        const mediaSheet = workbook.addWorksheet("Media");

        const { allRoomsClassifications, allRC_Rows } = preppedFuckingData;
        let currentCol = 1;

        let row2Vals = [];
        let rowIds = [];
        const mergeInstructions = Object.keys(allRoomsClassifications).reduce((acc, room) => {
            let numberOfClassifications;
            if (includeBefore) {
                row2Vals = [
                    ...row2Vals, 
                    ...(allRoomsClassifications[room].reduce((acc2, g) => [...acc2, g, ''], []))
                ];
                rowIds = [
                    ...rowIds, 
                    ...(allRoomsClassifications[room].reduce((acc2, g) => [...acc2, `${room}-${g}`, ''], []))
                ];
                numberOfClassifications = allRoomsClassifications[room].length*2;
            } else {
                row2Vals = [...row2Vals, ...allRoomsClassifications[room]];
                rowIds = [...rowIds, ...allRoomsClassifications[room].map((g) => `${room}-${g}`)];
                numberOfClassifications = allRoomsClassifications[room].length;
            }
            const startCol = currentCol;
            const endCol = currentCol + numberOfClassifications - 1;
            currentCol += numberOfClassifications;

            return [
                ...acc,
                {
                    title: room,
                    start: startCol,
                    end: endCol,
                }
            ]
        }, []);

        const headerRow1 = mediaSheet.addRow(mergeInstructions.map((item) => item.title));
        const headerRow2 = mediaSheet.addRow(row2Vals);

        const subHeaderStyle = {
            font: { ...baseStyle.font, bold: true },
            fill: {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFEFEFEF" }
            },
            border: borderAll(),
            alignment: { horizontal: "center" }
        };

        mergeInstructions.forEach(({ title, start, end }) => {
            if (start !== end) mediaSheet.mergeCells(headerRow1.number, start, headerRow1.number, end);
            const cell = mediaSheet.getCell(headerRow1.number, start);
            setCellStyling({
                cell,
                value: title,
                font: { ...baseStyle.font, bold: true },
                alignment: { horizontal: "center" },
                fill: {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFDDEEFF" }
                },
                border: borderAll(),
            });

            if (includeBefore) {
                allRoomsClassifications[title].forEach((classification, idx) => {
                    mediaSheet.mergeCells(headerRow2.number, start + idx*2, headerRow2.number, start + idx*2 + 1);
                    const subHeaderCell = mediaSheet.getCell(headerRow2.number, start + idx*2);
                    setCellStyling({
                        cell: subHeaderCell,
                        value: classification,
                        ...subHeaderStyle,
                    });
                });
            }
        });
        
        if (includeBefore) {
            const BA_RowVals = row2Vals.map((item) => item === "" ? "After" : "Before");
            const BA_Row = mediaSheet.addRow(BA_RowVals);
            BA_Row.eachCell((cell) => {
                setCellStyling({
                    cell,
                    ...subHeaderStyle
                });
            });
        }

        const addRows = {};
        let lastColIndex = 0;
        
        Object.keys(allRoomsClassifications).forEach((room, i) => {
            allRoomsClassifications[room].forEach((g, j) => {
                const groupRows = allRC_Rows[`${room}-${g}`];
                const imgs = groupRows[groupRows.length - 1];
                const beforeImgs = includeBefore ? groupRows[groupRows.length - 2] : null;
                imgs.forEach((img, idx) => {
                    const imgBefore = includeBefore ? beforeImgs?.[idx] : null;

                    if (!addRows?.[idx]) addRows[idx] = {};
                    if (includeBefore) {
                        addRows[idx][lastColIndex] = imgBefore;
                    }
                    addRows[idx][lastColIndex + (includeBefore ? 1 : 0)] = img;
                });
                lastColIndex += includeBefore ? 2 : 1;
            });
        });
        Object.keys(addRows).forEach((rowIdx) => {
            const addRow = addRows[rowIdx];
            const addRowCols = Object.keys(addRow).map((o) => Number(o));
            const maxCol = addRowCols.reduce((max, num) => (num > max ? num : max), addRowCols[0]);
            const mediaRowValues = (new Array(maxCol+1).fill(0)).map((_) => "Image");
            const mediaRow = mediaSheet.addRow(mediaRowValues);
            mediaRow.eachCell((cell, cellIndex) => {
                const item = addRow?.[cellIndex-1];
                const cellValue = item ? {value: {text: "Image", hyperlink: item}} : {value: '-'};
                const cellFont = {
                    ...baseStyle.font,
                    ...(item ? 
                        { underline: true, color: { argb: "FF0000FF" }} : 
                        { italic: true, color: { argb: "FF888888" } }
                    )
                };
                setCellStyling({
                    cell,
                    ...cellValue,
                    font: cellFont,
                    border: borderAll(),
                    alignment: { horizontal: "center" },
                    fill: {
                        ...baseStyle.fill,
                        fgColor: { argb: "FFFDFDFD" }
                    }
                });
            });
        });

        row2Vals.forEach((_, idx) => {
            mediaSheet.getColumn(idx + 1).width = 20;
        });

        return rowIds.map((item, idx) => [item, idx]).filter(([item, _]) => item !== "").reduce((acc, [item, idx]) => ({...acc, [item]: idx}), {});
    };

    const buildExcel = async ({
        header, previousInspectionDate=null, inspectionDate, reportItems, excludeBefore=false,
        customText, chargeAmount
    }) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Inspection Report');

        const baseStyle = {
            font: { name: "Calibri", size: 12 },
            alignment: { horizontal: "left" },
            border: borderAll(),
            fill: {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFEFEFEF" }
            }
        }
        const includeBefore = hasBaseline && !excludeBefore;

        const preppedFuckingData = prepFuckingData(reportItems, includeBefore);
        const rowIds = buildMediaWorksheet(workbook, preppedFuckingData, includeBefore, baseStyle);
        
        worksheet.mergeCells("A1:F1");
        const titleCell = worksheet.getCell("A1");
        setCellStyling({
            cell: titleCell,
            value: header,
            font: { ...baseStyle.font, bold: true, size: 16 },
            alignment: { horizontal: "left" },
        });

        worksheet.addRow([]);
        if (includeBefore) {
            const inspectionDateRow = worksheet.addRow([
                `Previous Inspection Date ${previousInspectionDate || ""}`, "",
                `Inspection Date ${inspectionDate || ""}`, "View Inspection"
            ]);
            inspectionDateRow.font = baseStyle.font;
            if (includeVideoInExport) {
                inspectionDateRow.getCell(4).value = {text: "View Inspection", hyperlink: videoSrc};
            } else {
                inspectionDateRow.getCell(4).value = {text: ""};
            }
        } else {
            worksheet.addRow([`Inspection Date ${inspectionDate || ""}`]).font = baseStyle.font;
        }
        worksheet.addRow([`Resident Name: ${guestData.name || "N/A"}`]).font = baseStyle.font;
        worksheet.addRow([]);
        console.log("Charge Amount: ", chargeAmount);
        if (chargeAmount > 0) {
            const chargeTitle = worksheet.addRow(["Charge Details"]);
            const totalCharge = worksheet.addRow(["Total Charge:", chargeAmount]);
            chargeTitle.eachCell((cell) => setCellStyling({
                cell, font: { ...baseStyle.font, bold: true },
            }));
            totalCharge.eachCell((cell) => setCellStyling({ cell, font: baseStyle.font }));
            totalCharge.getCell(2).numFmt = '"$"#,##0.00';
            // totalCharge.getCell(2).font = { ...baseStyle.font, color: { argb: "FF0000FF" } };
            worksheet.addRow([]);
        }
        if ((customText || "").length > 0) {
            worksheet.addRow(["Note:"]);
            const noteRow = worksheet.addRow([customText]);
            worksheet.mergeCells(`A${noteRow.number}:G${noteRow.number}`);
            noteRow.eachCell((cell) => setCellStyling({ 
                cell, 
                font: baseStyle.font, 
                alignment: {
                    horizontal: "left",
                    wrapText: true
                }
            }));
            worksheet.addRow([]);
        }

        // Resident Details
        const labelRow = worksheet.addRow(["Move In", "Move Out", "Deposit", "Rent"]);
        const valueRow = worksheet.addRow([
            dateTimeFormater(guestData.checkin, DATE_FORMAT),
            dateTimeFormater(guestData.checkout, DATE_FORMAT),
            (guestData?.deposit ?
                Number(guestData.deposit).toLocaleString(
                    "en-US", 
                    { style: "currency", currency: selectedCurrency === 'eur' ? 'EUR' : 'USD', currencyDisplay: 'symbol' }
                ) : "N/A"
            ),
            (guestData?.rent ?
                Number(guestData.rent).toLocaleString(
                    "en-US", 
                    { style: "currency", currency: selectedCurrency === 'eur' ? 'EUR' : 'USD', currencyDisplay: 'symbol' }
                ) : "N/A"
            )
        ]);

        labelRow.eachCell((cell) => setCellStyling({
            cell, 
            font: { ...baseStyle.font, bold: true },
            fill: {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFEFEFEF" }
            },
            border: borderAll(),
            alignment: { horizontal: "center" }
        }));

        valueRow.getCell(1).numFmt = DATE_FORMAT;
        valueRow.getCell(2).numFmt = DATE_FORMAT;
        valueRow.getCell(3).numFmt = '"$"#,##0.00';
        valueRow.getCell(4).numFmt = '"$"#,##0.00';

        valueRow.eachCell((cell) => setCellStyling({
            cell, 
            font: baseStyle.font,
            border: borderAll(),
            alignment: { horizontal: "center" }
        }));

        worksheet.addRow([]);
        worksheet.addRow([]);

        // Report Headers
        const headerRow = worksheet.addRow(
            includeBefore ?
            ["Area", "Condition", "Comment", "Price", "Before Media", "Media"] :
            ["Area", "Condition", "Comment", "Price", "Media"]
        );
        headerRow.eachCell((cell) => setCellStyling({
            cell,
            font: { ...baseStyle.font, bold: true },
            fill: {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFDDDDDD" }
            },
            border: borderAll(),
            alignment: { horizontal: "center" }
        }))

        const roomStyle = {
            font: { ...baseStyle.font, bold: true },
            fill: {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFDDDDDD" }
            },
            alignment: { horizontal: "left" }
        };
        
        // Report Body
        if (centralizedView) {
            const { roomsList, allRoomsClassifications, allRC_Items, allRC_Rows } = preppedFuckingData;
            // Iterating over the rooms
            for (let roomIdx=0; roomIdx < roomsList.length; roomIdx++) {
                const room = roomsList[roomIdx];
                const roomsClassifications = allRoomsClassifications[room];
                const classificationItems = allRC_Items[room];

                const roomHeaderRow = worksheet.addRow([room]);
                if (includeBefore) {
                    worksheet.mergeCells(`A${roomHeaderRow.number}:F${roomHeaderRow.number}`);
                } else {
                    worksheet.mergeCells(`A${roomHeaderRow.number}:E${roomHeaderRow.number}`);
                }
                const roomTitleCell = worksheet.getCell(`A${roomHeaderRow.number}`);
                setCellStyling({
                    cell: roomTitleCell,
                    value: room,
                    ...roomStyle,
                });

                for (let gIdx=0; gIdx < roomsClassifications.length; gIdx++) {
                    // "Area", "Condition", "Comment", "Price", ?"Before Media", "Media"
                    const gName = roomsClassifications[gIdx];
                    const LetsRow = allRC_Rows[`${room}-${gName}`];
                    const giRow = worksheet.addRow([
                        ...LetsRow.slice(0, 4), 
                        ...(includeBefore ? ["View", "View"] : ["View"])
                    ]);

                    giRow.eachCell((cell) => setCellStyling({
                        cell, font: baseStyle.font, border: borderAll(),
                        alignment: { horizontal: "left" },
                        fill: {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "FFF7F7F7" }
                        }
                    }));
                    giRow.getCell(3).alignment.wrapText = true;
                    giRow.getCell(4).numFmt = '"$"#,##0.00';

                    const mediaColIdx = rowIds[`${room}-${gName}`];
                    const mediaColLetter = String.fromCharCode(65 + mediaColIdx);
                    const mediaRowNumber = 2;
                    if (includeBefore) {
                        setCellStyling({
                            cell: giRow.getCell(5), 
                            value: {text: "View", hyperlink: `#Media!${mediaColLetter}${mediaRowNumber}`},
                            font: { ...baseStyle.font, color: { argb: "FF0000FF" }, underline: true },
                        });
                        const media2ColLetter = String.fromCharCode(66 + mediaColIdx);
                        setCellStyling({
                            cell: giRow.getCell(6), 
                            value: {text: "View", hyperlink: `#Media!${media2ColLetter}${mediaRowNumber}`},
                            font: { ...baseStyle.font, color: { argb: "FF0000FF" }, underline: true },
                        });
                    } else {
                        setCellStyling({
                            cell: giRow.getCell(5), 
                            value: {text: "View", hyperlink: `#Media!${mediaColLetter}${mediaRowNumber}`},
                            font: { ...baseStyle.font, color: { argb: "FF0000FF" }, underline: true },
                        });
                    }
                }
            }

            // Column widths
            const widths = [30, 18, 50, 12, 12];
            widths.forEach((w, i) => {
                worksheet.getColumn(i + 1).width = w;
            });

            worksheet.views = [{ state: 'frozen', ySplit: 4 }];

            // Create the workbook buffer
            const buffer = await workbook.xlsx.writeBuffer();

            // Turn buffer into Blob
            const blob = new Blob(
                [buffer], 
                { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
            );

            return blob;
        }
        return null;
    };

    const preHandleExport = () => {
        if (editExportMode && !finishExportMode) {
            handleNotifState({
                text: 'You are in edit mode. Please save your changes before proceeding.',
                type: SeverityLevels.ERROR
            });
            handleCloseMenu();
            return;
        }
        
        setLoading(true);
        // const selectedIssues = Object.keys(checkboxStates).filter((object_id) => checkboxStates[object_id]);
        // const selectedMetadata = Object.fromEntries(
        //     selectedIssues.map((object_id) => [object_id, getDefaultMetadata(itemMetadata[object_id])])
        // );
        const sectionKeys = [ReportsCategory.INVENTORY, ReportsCategory.ORGANIZATION, ReportsCategory.DEFECTS];

        let reportItems = {};
        let totalCharge;
        if (centralizedView) {
            const roomsList = roomsData.reduce((acc1, r) => {
                if (!acc1.includes(r.title)) acc1.push(r.title);
                return acc1;
            }, []);
            const selectedItems = Object.entries(
                (checkboxStates[ReportsCategory.CENTRALIZED] || {})
            ).filter(([object_id, checked]) => checked).map(([object_id, checked]) => object_id);
            
            reportItems = {
                [ReportsCategory.CENTRALIZED]: {
                    roomsList: roomsList,
                    items: roomsList.reduce((acc1, room) => {
                        const roomData = orderedData[ReportsCategory.CENTRALIZED].filter(
                            (item) => item.room === room
                        ).filter(
                            (item) => selectedItems.includes(item.object_id)
                        );
                        const roomIdentifications = roomData.map((item) => item.object_id);
                        const roomClassifications = [...new Set(
                            roomIdentifications.map((id) => classifiedCategories?.[id]).filter((c) => c)
                        )];
                        roomClassifications.sort();
                        
                        if (roomClassifications.length === 0 ) return acc1;
                        roomClassifications.forEach((g) => {
                            const addToCharge = (parseFloat(itemMetadata?.[ReportsCategory.CENTRALIZED]?.[`${room}-${g}`]?.price) || 0);
                            console.log("Charge for ", g, " in ", room, ": ", addToCharge);
                            totalCharge += addToCharge;
                            console.log("Total Charge: ", totalCharge);
                        });
                        
                        return {
                            ...acc1,
                            [room]: {
                                classifications: roomClassifications.reduce((acc2, classification) => ({
                                    ...acc2,
                                    [classification]: {
                                        description: itemMetadata?.[ReportsCategory.CENTRALIZED]?.[`${room}-${classification}`]?.description,
                                        price: itemMetadata?.[ReportsCategory.CENTRALIZED]?.[`${room}-${classification}`]?.price,
                                        ...(roomData?.[0]?.before_date ? {before_date: roomData?.[0]?.before_date.split("-").reverse().join("-")} : {})
                                    }
                                }), {}),
                                items: (
                                    roomData.map( (item) => {
                                        const isDefect = item.issue_type === 'Defect';
                                        const metadata = getDefaultMetadata(itemMetadata?.[ReportsCategory.CENTRALIZED]?.[item.object_id]);
                                        return {
                                            img: (!hasBaseline || isDefect) ? `${item.media}&zoom_out=true` : item.before_media,
                                            ...(hasBaseline ? 
                                                { 
                                                    img_before: !isDefect ? `${item.media}&zoom_out=true` : item.before_media, 
                                                    before_date: item.before_date ? item.before_date.split("-").reverse().join("-") : item.before_date 
                                                } : {}
                                            ),
                                            object_id: item.object_id,
                                            subject: metadata?.title ? metadata.title : item.subject,
                                            issue_type: item.issue_type,
                                            classifiedCategory: item?.classifiedCategory,
                                            room: item?.room,
                                        };
                                    })
                                )
                            }
                        }
                    }, {})
                }
            };
        } else {
            reportItems = sectionKeys.reduce((acc, sectionKey) => {
                const sectionItems = reportData[sectionKey]?.All || reportData[sectionKey] || [];
                const selectedItems = Object.entries((checkboxStates[sectionKey] || {})).filter(([object_id, checked]) => checked).map(([object_id, checked]) => object_id);
                acc[sectionKey] = sectionItems.filter((item) => selectedItems.includes(item.object_id)).map(
                    (item) => {
                        const isDefect = item.issue_type === 'Defect';
                        const metadata = getDefaultMetadata(itemMetadata?.[sectionKey]?.[item.object_id]);
                        return {
                            img: (!hasBaseline || isDefect) ? `${item.media}&zoom_out=true` : item.before_media,
                            ...(hasBaseline ? { img_before: !isDefect ? `${item.media}&zoom_out=true` : item.before_media, before_date: item.before_date ? item.before_date.split("-").reverse().join("-") : item.before_date } : {}),
                            object_id: item.object_id,
                            subject: metadata?.title ? metadata.title : item.subject,
                            issue_type: item.issue_type,
                            room: metadata.room,
                            description: metadata.description,
                            price: metadata.price,
                        };
                    }
                );
                return acc;
            }, {});
            
            totalCharge = Object.values(reportItems).flat().reduce((sum, item) => sum + (parseFloat(item.price) || 0), 0);
        }
        
        return [reportItems, totalCharge];
    };

    const getExportBlob = async () => {
        let excelDoc = null;
        try {
            const preHandleData = preHandleExport();

            if (!preHandleData) {
                console.log("Failed to prepare Excel file");
                return null;
            }

            const [reportItems, totalCharge] = preHandleData;
            if (centralizedView) {
                excelDoc = await buildExcel({
                    header,
                    inspectionDate: date,
                    tenantDetails: {},
                    reportItems: reportItems[ReportsCategory.CENTRALIZED],
                    excludeBefore: !hasBaseline,
                    customText,
                    chargeAmount: totalCharge
                });
            }
        } catch (err) {
            handleNotifState({
                text: 'An error occurred while exporting the Excel report.',
                type: SeverityLevels.ERROR
            });
            console.log(err)
        } finally {
            if (isMounted.current) {
                setLoading(false);
                // dispatch(resetEditMode());
                handleCloseMenu();
                // dispatch(reseItemMetadata())
            }
        }
        return excelDoc;
    }

    const handleExport = async () => {
        try {
            const preHandleData = preHandleExport();

            if (!preHandleData) {
                console.log("Failed to prepare Excel file");
                return;
            }
            
            const [reportItems, totalCharge] = preHandleData;

            if (centralizedView) {
                const excelDoc = await buildExcel({
                    header,
                    inspectionDate: date,
                    tenantDetails: {},
                    reportItems: reportItems[ReportsCategory.CENTRALIZED],
                    excludeBefore: !hasBaseline,
                    customText,
                    chargeAmount: totalCharge
                });
                
                if (isMounted.current && excelDoc) {
                    const XL_URL = window.URL.createObjectURL(excelDoc);
                    const downloadLink = document.createElement('a');
                    downloadLink.href = XL_URL;
                    downloadLink.setAttribute('download', `Report__${header.replace(/\s/g, "_")}_${date.replace(/\//g, "-")}.xlsx`);
                    downloadLink.click();
                } else if (!excelDoc) {
                    handleNotifState({
                        text: "Can't create an excel export for this type of report.",
                        type: SeverityLevels.ERROR
                    });
                } else {
                    handleNotifState({
                        text: 'An error occurred while generating the Excel file.',
                        type: SeverityLevels.ERROR
                    });
                }
            }
            
        } catch (err) {
            handleNotifState({
                text: 'An error occurred while exporting the Excel report.',
                type: SeverityLevels.ERROR
            });
            console.log(err)
        } finally {
            if (isMounted.current) {
                setLoading(false);
                // dispatch(resetEditMode());
                handleCloseMenu();
                // dispatch(reseItemMetadata())
            }
        }
    }

    return {
        loading,
        handleExport,
        getExportBlob,
    };
};

export default useXlsxGenerator;